import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CurrencyDollarIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  BanknotesIcon
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import LoadingSpinner from '../components/LoadingSpinner';
import useApi from '../hooks/useApi';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function Commissions() {
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showPayoutModal, setShowPayoutModal] = useState(null);
  const limit = 20;

  // Fetch commissions
  const { data, loading, error, refetch } = useApi(
    `/api/admin/commissions?status=${statusFilter !== 'all' ? statusFilter : ''}&page=${page}&limit=${limit}`,
    [statusFilter, page]
  );

  const commissions = data?.data?.commissions || [];
  const totalPages = data?.data?.totalPages || 1;
  const total = data?.data?.total || 0;

  const handleApprove = async (id) => {
    if (!window.confirm('Are you sure you want to approve this commission?')) {
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/api/admin/commissions/${id}/approve`);
      toast.success('Commission approved successfully');
      refetch();
    } catch (error) {
      console.error('Error approving commission:', error);
      toast.error('Failed to approve commission');
    }
  };

  const handlePay = async (id, payoutReference) => {
    try {
      await axios.put(`${API_BASE_URL}/api/admin/commissions/${id}/pay`, {
        payoutReference
      });
      toast.success('Commission marked as paid successfully');
      setShowPayoutModal(null);
      refetch();
    } catch (error) {
      console.error('Error marking commission as paid:', error);
      toast.error('Failed to mark commission as paid');
    }
  };

  const handleCancel = async (id) => {
    if (!window.confirm('Are you sure you want to cancel this commission? This action cannot be undone.')) {
      return;
    }

    try {
      await axios.put(`${API_BASE_URL}/api/admin/commissions/${id}/cancel`, {
        notes: 'Cancelled by admin'
      });
      toast.success('Commission cancelled successfully');
      refetch();
    } catch (error) {
      console.error('Error cancelling commission:', error);
      toast.error('Failed to cancel commission');
    }
  };

  const statusTabs = [
    { id: 'all', label: 'All', icon: CurrencyDollarIcon },
    { id: 'pending', label: 'Pending', icon: ClockIcon },
    { id: 'approved', label: 'Approved', icon: CheckCircleIcon },
    { id: 'paid', label: 'Paid', icon: BanknotesIcon },
    { id: 'cancelled', label: 'Cancelled', icon: XCircleIcon }
  ];

  // Calculate stats
  const stats = {
    pending: {
      count: commissions.filter(c => c.status === 'pending').length,
      amount: commissions.filter(c => c.status === 'pending').reduce((sum, c) => sum + parseFloat(c.amount), 0)
    },
    approved: {
      count: commissions.filter(c => c.status === 'approved').length,
      amount: commissions.filter(c => c.status === 'approved').reduce((sum, c) => sum + parseFloat(c.amount), 0)
    },
    paid: {
      count: commissions.filter(c => c.status === 'paid').length,
      amount: commissions.filter(c => c.status === 'paid').reduce((sum, c) => sum + parseFloat(c.amount), 0)
    }
  };

  const columns = [
    {
      key: 'triggered_at',
      label: 'Date',
      render: (commission) => (
        <div className="text-sm">
          {new Date(commission.triggered_at).toLocaleDateString()}
        </div>
      )
    },
    {
      key: 'affiliate',
      label: 'Affiliate',
      render: (commission) => (
        <div>
          <Link
            to={`/affiliates/${commission.affiliate_id}`}
            className="font-medium text-purple-600 hover:text-purple-900"
          >
            {commission.affiliate_name}
          </Link>
          <div className="text-sm text-gray-500 font-mono">{commission.affiliate_code}</div>
        </div>
      )
    },
    {
      key: 'user',
      label: 'User',
      render: (commission) => (
        <div>
          <div className="font-medium">{commission.user_name}</div>
          <div className="text-sm text-gray-500">{commission.user_email}</div>
        </div>
      )
    },
    {
      key: 'plan',
      label: 'Plan',
      render: (commission) => (
        <div>
          <div className="font-medium capitalize">{commission.plan_type}</div>
          <div className="text-sm text-gray-500 capitalize">{commission.billing_period}</div>
          <div className="text-xs text-gray-400">{commission.payment_count} payment(s)</div>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (commission) => (
        <span className="font-bold text-lg text-purple-600">
          ${parseFloat(commission.amount).toFixed(2)}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (commission) => (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold capitalize ${
          commission.status === 'paid'
            ? 'bg-green-100 text-green-800'
            : commission.status === 'approved'
            ? 'bg-blue-100 text-blue-800'
            : commission.status === 'pending'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {commission.status === 'paid' && <CheckCircleIcon className="h-3 w-3 mr-1" />}
          {commission.status === 'pending' && <ClockIcon className="h-3 w-3 mr-1" />}
          {commission.status === 'approved' && <CheckCircleIcon className="h-3 w-3 mr-1" />}
          {commission.status === 'cancelled' && <XCircleIcon className="h-3 w-3 mr-1" />}
          {commission.status}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (commission) => (
        <div className="flex flex-col space-y-1">
          {commission.status === 'pending' && (
            <>
              <button
                onClick={() => handleApprove(commission.id)}
                className="text-blue-600 hover:text-blue-900 text-sm font-medium text-left"
              >
                Approve
              </button>
              <button
                onClick={() => handleCancel(commission.id)}
                className="text-red-600 hover:text-red-900 text-sm font-medium text-left"
              >
                Cancel
              </button>
            </>
          )}
          {commission.status === 'approved' && (
            <>
              <button
                onClick={() => setShowPayoutModal(commission)}
                className="text-green-600 hover:text-green-900 text-sm font-medium text-left"
              >
                Mark as Paid
              </button>
              <button
                onClick={() => handleCancel(commission.id)}
                className="text-red-600 hover:text-red-900 text-sm font-medium text-left"
              >
                Cancel
              </button>
            </>
          )}
          {commission.status === 'paid' && commission.payout_reference && (
            <span className="text-xs text-gray-500">
              Ref: {commission.payout_reference}
            </span>
          )}
        </div>
      )
    }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <CurrencyDollarIcon className="h-8 w-8 mr-3 text-purple-600" />
            Commissions
          </h1>
          <p className="mt-2 text-gray-600">
            Manage affiliate commissions and payouts
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-500">Total Commissions</div>
                <div className="mt-2 text-3xl font-bold text-gray-900">{total}</div>
              </div>
              <CurrencyDollarIcon className="h-12 w-12 text-gray-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-500">Pending</div>
                <div className="mt-2 text-3xl font-bold text-yellow-600">{stats.pending.count}</div>
                <div className="text-sm text-gray-500">${stats.pending.amount.toFixed(2)}</div>
              </div>
              <ClockIcon className="h-12 w-12 text-yellow-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-500">Approved</div>
                <div className="mt-2 text-3xl font-bold text-blue-600">{stats.approved.count}</div>
                <div className="text-sm text-gray-500">${stats.approved.amount.toFixed(2)}</div>
              </div>
              <CheckCircleIcon className="h-12 w-12 text-blue-400" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-gray-500">Paid</div>
                <div className="mt-2 text-3xl font-bold text-green-600">{stats.paid.count}</div>
                <div className="text-sm text-gray-500">${stats.paid.amount.toFixed(2)}</div>
              </div>
              <BanknotesIcon className="h-12 w-12 text-green-400" />
            </div>
          </div>
        </div>

        {/* Filters and Table */}
        <div className="bg-white rounded-lg shadow">
          {/* Status Tabs */}
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {statusTabs.map((tab) => {
                const Icon = tab.icon;
                const count = tab.id === 'all' ? total : commissions.filter(c => c.status === tab.id).length;
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setStatusFilter(tab.id);
                      setPage(1);
                    }}
                    className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm transition-colors ${
                      statusFilter === tab.id
                        ? 'border-purple-500 text-purple-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-center space-x-2">
                      <Icon className="h-5 w-5" />
                      <span>{tab.label}</span>
                      <span className="px-2 py-0.5 rounded-full bg-gray-100 text-xs">
                        {count}
                      </span>
                    </div>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Table */}
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <LoadingSpinner size="large" />
            </div>
          ) : error ? (
            <div className="text-center py-12 text-red-600">
              Error loading commissions: {error.message}
            </div>
          ) : commissions.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No commissions found for this status
            </div>
          ) : (
            <>
              <Table columns={columns} data={commissions} />
              
              {totalPages > 1 && (
                <div className="p-6 border-t">
                  <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Payout Modal */}
      {showPayoutModal && (
        <PayoutModal
          commission={showPayoutModal}
          onClose={() => setShowPayoutModal(null)}
          onSubmit={(payoutReference) => handlePay(showPayoutModal.id, payoutReference)}
        />
      )}
    </AdminLayout>
  );
}

// Payout Modal Component
function PayoutModal({ commission, onClose, onSubmit }) {
  const [payoutReference, setPayoutReference] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!payoutReference.trim()) {
      toast.error('Please enter a payout reference');
      return;
    }

    setLoading(true);
    await onSubmit(payoutReference);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Mark Commission as Paid</h3>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Affiliate:</span>
              <span className="text-sm font-medium">{commission.affiliate_name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Amount:</span>
              <span className="text-sm font-bold text-purple-600">${parseFloat(commission.amount).toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">User:</span>
              <span className="text-sm">{commission.user_name}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payout Reference / Transaction ID *
            </label>
            <input
              type="text"
              required
              value={payoutReference}
              onChange={(e) => setPayoutReference(e.target.value)}
              placeholder="e.g., PAYPAL-TXN-12345 or Check #789"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter the payment transaction ID, check number, or other reference
            </p>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : 'Mark as Paid'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Commissions;

